import React, { useState, useEffect, useReducer, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  Collapse,
  Paper,
  Drawer,
} from "@mui/material";
import {
  IconArrowLeft,
  IconPlus,
  IconCalculator,
  IconX,
} from "@tabler/icons-react";
import { ProductCalculator } from "./ProductCalculator";
import useMediaQuery from "@mui/material/useMediaQuery";

type Calculator = {
  id: string;
  name: string;
  price: string;
};

type CalculatorAction =
  | { type: "ADD_CALCULATOR" }
  | {
      type: "UPDATE_CALCULATOR";
      id: string;
      field: keyof Calculator;
      value: string;
    }
  | { type: "REMOVE_CALCULATOR"; id: string }
  | { type: "LOAD_CALCULATORS"; calculators: Calculator[] };

// Reducer para manejar el estado de las calculadoras
const calculatorsReducer = (
  state: Calculator[],
  action: CalculatorAction
): Calculator[] => {
  switch (action.type) {
    case "ADD_CALCULATOR":
      return [
        ...state,
        {
          id: Date.now().toString(),
          name: `Producto ${state.length + 1}`,
          price: "",
        },
      ];
    case "UPDATE_CALCULATOR":
      return state.map((calc) =>
        calc.id === action.id ? { ...calc, [action.field]: action.value } : calc
      );
    case "REMOVE_CALCULATOR":
      return state.length > 1
        ? state.filter((calc) => calc.id !== action.id)
        : state;
    case "LOAD_CALCULATORS":
      return action.calculators;
    default:
      return state;
  }
};

const STORAGE_KEY = "yanbal-calculators";

const YanbalCalc = () => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();

  const [calculators, dispatch] = useReducer(calculatorsReducer, []);
  const [isInitialized, setIsInitialized] = useState(false);
  const [openSumModal, setOpenSumModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const listEndRef = React.useRef<HTMLDivElement>(null);

  // Cargar calculadoras desde localStorage al inicio
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          dispatch({ type: "LOAD_CALCULATORS", calculators: parsed });
          return;
        }
      }
      // Si no hay datos guardados, crear una calculadora por defecto
      dispatch({ type: "ADD_CALCULATOR" });
    } catch (error) {
      console.error("Error al cargar calculadoras:", error);
      dispatch({ type: "ADD_CALCULATOR" });
    } finally {
      setIsInitialized(true);
    }
  }, []);

  // Guardar en localStorage cuando cambien las calculadoras
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(calculators));
    }
  }, [calculators, isInitialized]);

  // Hacer scroll al final cuando se agregue una nueva calculadora
  useEffect(() => {
    if (calculators.length > 1 && listEndRef.current) {
      listEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [calculators.length]);

  // Memoizar las funciones de manejo para evitar recrearlas en cada render
  const handleNameChange = useCallback((id: string, value: string) => {
    dispatch({ type: "UPDATE_CALCULATOR", id, field: "name", value });
  }, []);

  const handleAmountChange = useCallback((id: string, value: string) => {
    dispatch({ type: "UPDATE_CALCULATOR", id, field: "price", value });
  }, []);

  const handleRemove = useCallback((id: string) => {
    dispatch({ type: "REMOVE_CALCULATOR", id });
  }, []);

  const handleToggleProduct = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Calcular totales
  const selectedCalculators = calculators.filter((c) =>
    selectedIds.includes(c.id)
  );
  // Sumar los montos originales en USD
  const totalAmount = selectedCalculators.reduce(
    (sum, c) => sum + (parseFloat(c.price) || 0),
    0
  );
  // Calcular el total en Bs a partir de la suma en USD (redondear SIEMPRE hacia arriba)
  const totalBs = Math.ceil(totalAmount * 6.96);

  // Seleccionar todos por defecto al abrir el modal
  useEffect(() => {
    if (openSumModal) {
      setSelectedIds(calculators.map((c) => c.id));
      setExpandedRows([]);
    }
  }, [openSumModal, calculators]);

  // Tabla de porcentajes idéntica a la de ProductCalculator
  const PorcentajeTable = ({ total }: { total: number }) => {
    // total ya viene redondeado hacia arriba
    const inversion = Math.ceil(total * 0.25);
    const ofertaMaxima = Math.ceil(total * 0.15);
    const ofertaMinima = Math.ceil(total * 0.1);
    return (
      <Box
        component={Paper}
        variant="outlined"
        sx={{ p: 2, borderRadius: 1, mt: 1 }}
      >
        {/* 10% Oferta Mínima */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "medium" }}
          >
            Oferta Mín. (10%):
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography>{ofertaMinima.toLocaleString("es-BO")} Bs.</Typography>
            <Typography color="text.secondary">|</Typography>
            <Typography>
              {(total - ofertaMinima).toLocaleString("es-BO")} Bs.
            </Typography>
          </Box>
        </Box>
        {/* 15% Oferta Máxima */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "medium" }}
          >
            Oferta Máx. (15%):
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography>{ofertaMaxima.toLocaleString("es-BO")} Bs.</Typography>
            <Typography color="text.secondary">|</Typography>
            <Typography>
              {(total - ofertaMaxima).toLocaleString("es-BO")} Bs.
            </Typography>
          </Box>
        </Box>
        {/* 25% Inversión */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: "bold",
            backgroundColor: "rgba(0,0,0,0.04)",
            p: 1,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold" }}
          >
            Inversión (25%):
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {inversion.toLocaleString("es-BO")} Bs.
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ fontWeight: "bold" }}
            >
              |
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {(total - inversion).toLocaleString("es-BO")} Bs.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  if (!isInitialized) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: 2, py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
        <Tooltip title="Volver">
          <IconButton
            onClick={() => navigate("/tools")}
            sx={{ color: "primary.main" }}
          >
            <IconArrowLeft size={24} />
          </IconButton>
        </Tooltip>

        <Typography
          variant="h5"
          component="h1"
          sx={{ flexGrow: 1, color: theme.palette.primary.main }}
        >
          Calculadora
        </Typography>

        <Tooltip title="Agregar Producto">
          <IconButton
            onClick={() => dispatch({ type: "ADD_CALCULATOR" })}
            color="primary"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            <IconPlus size={20} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Suma total de productos">
          <IconButton
            onClick={() => setOpenSumModal(true)}
            color="primary"
            sx={{
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.success.main,
              },
              ml: 1,
            }}
          >
            <IconCalculator size={20} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid
        container
        spacing={3}
      >
        {calculators.map((calculator) => (
          <Grid
            item
            xs={12}
            md={6}
            key={calculator.id}
          >
            <ProductCalculator
              key={calculator.id}
              id={calculator.id}
              name={calculator.name}
              price={calculator.price}
              onNameChange={handleNameChange}
              onAmountChange={handleAmountChange}
              onRemove={handleRemove}
              canRemove={calculators.length > 1}
            />
          </Grid>
        ))}
        <div ref={listEndRef} />
      </Grid>

      {/* Modal/Drawer para suma total de productos */}
      {isDesktop ? (
        <Drawer
          anchor="right"
          open={openSumModal}
          onClose={() => setOpenSumModal(false)}
          PaperProps={{
            sx: {
              width: 420,
              maxWidth: "100vw",
              height: "100%", // Alto completo
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
              p: 0,
              background: "#fff",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              color: "#0d6336",
              fontWeight: 600,
              pt: 4,
              pb: 2,
              position: "relative",
              fontSize: "1.25rem",
              minHeight: 48,
            }}
          >
            Suma total de productos
            <IconButton
              onClick={() => setOpenSumModal(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#0d6336",
                backgroundColor: "transparent",
                boxShadow: "none",
                p: 1,
                "&:hover": {
                  backgroundColor: "rgba(244,67,54,0.08)",
                },
              }}
              aria-label="Cerrar"
            >
              <IconX size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              pt: 0,
              px: 3,
              pb: 2,
              minHeight: 0,
              maxHeight: "100%",
              flex: 1,
              overflowY: "auto",
            }}
          >
            <List
              dense
              sx={{ mb: 2 }}
            >
              {calculators.map((calc) => {
                const isSelected = selectedIds.includes(calc.id);
                const isRowExpanded = expandedRows.includes(calc.id);
                const bsAmount = parseFloat(calc.price)
                  ? Math.ceil(parseFloat(calc.price) * 6.96)
                  : 0;
                return (
                  <React.Fragment key={calc.id}>
                    <ListItem
                      disableGutters
                      sx={{
                        borderRadius: 2,
                        mb: 1.5,
                        bgcolor: isSelected
                          ? theme.palette.action.selected
                          : "background.paper",
                        transition: "background 0.2s, box-shadow 0.2s",
                        px: isSmallMobile ? 1.5 : 2.5,
                        py: isSmallMobile ? 1.2 : 1.7,
                        boxShadow: isSelected
                          ? "0 2px 8px 0 rgba(13,99,54,0.10)"
                          : "0 1px 4px 0 rgba(60,42,32,0.04)",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        // Evitar que el click en el chevron dispare el toggle de selección
                        if ((e.target as HTMLElement).closest(".chevron-btn"))
                          return;
                        handleToggleProduct(calc.id);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          onChange={() => handleToggleProduct(calc.id)}
                          tabIndex={-1}
                          disableRipple
                          color="primary"
                          sx={{ p: isSmallMobile ? 0.5 : 1 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemIcon>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flex: 1,
                          minWidth: 0,
                          justifyContent: "space-between",
                          gap: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: isSmallMobile ? "1.05rem" : "1.1rem",
                            color: "text.primary",
                            flex: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {calc.name}
                        </Typography>
                        {calc.price && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                              ml: 2,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 999,
                              bgcolor: "white",
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                color: isSelected
                                  ? "primary.main"
                                  : "text.secondary",
                                fontWeight: 600,
                                fontSize: isSmallMobile ? "0.98rem" : "1.03rem",
                                whiteSpace: "nowrap",
                                letterSpacing: 0.2,
                              }}
                            >
                              $
                              {parseFloat(calc.price).toLocaleString("es-BO", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              | Bs {bsAmount}
                            </Typography>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          className="chevron-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedRows((prev) =>
                              prev.includes(calc.id)
                                ? prev.filter((id) => id !== calc.id)
                                : [...prev, calc.id]
                            );
                          }}
                          sx={{
                            ml: 2,
                            flexShrink: 0,
                            borderRadius: 1.5,
                            p: 0.7,
                            bgcolor: isRowExpanded
                              ? "rgba(13,99,54,0.08)"
                              : "transparent",
                            transition: "background 0.2s",
                          }}
                          aria-label={isRowExpanded ? "Colapsar" : "Expandir"}
                        >
                          {isRowExpanded ? (
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="18 15 12 9 6 15"></polyline>
                              </svg>
                            </span>
                          ) : (
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          )}
                        </IconButton>
                      </Box>
                    </ListItem>
                    <Collapse
                      in={isRowExpanded && isSelected}
                      timeout="auto"
                      unmountOnExit
                    >
                      <PorcentajeTable total={bsAmount} />
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
            {/* Total como en ProductCalculator, pero con funcionalidad de mostrar tabla al click */}
            <Box
              sx={{
                textAlign: "center",
                userSelect: "none",
                mt: 3,
                mb: 2,
                cursor: "pointer",
                "&:hover .operation": { color: "primary.main" },
              }}
              onClick={() =>
                setExpandedRows((prev) =>
                  prev.includes("total")
                    ? prev.filter((id) => id !== "total")
                    : [...prev, "total"]
                )
              }
            >
              <Typography
                variant="h6"
                className="operation"
                sx={{
                  color: "text.secondary",
                  mb: 1,
                  transition: "color 0.2s",
                }}
              >
                $
                {totalAmount.toLocaleString("es-BO", {
                  maximumFractionDigits: 2,
                })}{" "}
                × 6.96
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", color: "primary.main" }}
              >
                = {totalBs.toLocaleString("es-BO")} Bs.
              </Typography>
            </Box>
            <Collapse in={expandedRows.includes("total")}>
              <PorcentajeTable total={totalBs} />
            </Collapse>
          </DialogContent>
        </Drawer>
      ) : (
        <Dialog
          open={openSumModal}
          onClose={() => setOpenSumModal(false)}
          maxWidth="xs"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: isSmallMobile
              ? {
                  borderRadius: 0,
                  m: 0,
                  width: "100vw",
                  height: "100vh",
                  maxWidth: "100vw",
                  maxHeight: "100vh",
                  p: 1,
                  boxShadow: "none",
                }
              : {
                  borderRadius: 2,
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                  margin: 2,
                },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              color: "#0d6336",
              fontWeight: 600,
              pt: isSmallMobile ? 2 : 4,
              pb: isSmallMobile ? 1 : 2,
              position: "relative",
              fontSize: isSmallMobile ? "1.1rem" : "1.25rem",
              minHeight: isSmallMobile ? 48 : undefined,
            }}
          >
            Suma total de productos
            <IconButton
              onClick={() => setOpenSumModal(false)}
              sx={{
                position: "absolute",
                top: isSmallMobile ? 6 : 8,
                right: isSmallMobile ? 6 : 8,
                color: isSmallMobile ? "#0d6336" : "white",
                backgroundColor: isSmallMobile ? "transparent" : "#3c2a20",
                boxShadow: "none",
                p: isSmallMobile ? 0.5 : 1,
                "&:hover": {
                  backgroundColor: isSmallMobile
                    ? "rgba(244,67,54,0.08)"
                    : "#0d6336",
                },
              }}
              aria-label="Cerrar"
            >
              <IconX size={isSmallMobile ? 28 : 20} />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              pt: isSmallMobile ? 0 : 0,
              px: isSmallMobile ? 1 : 3,
              pb: isSmallMobile ? 1 : 2,
              minHeight: isSmallMobile ? "calc(100vh - 110px)" : undefined,
              maxHeight: isSmallMobile ? "none" : 400,
              overflowY: isSmallMobile ? "auto" : "visible",
            }}
          >
            <List
              dense
              sx={{ mb: isSmallMobile ? 1 : 2 }}
            >
              {calculators.map((calc) => {
                const isSelected = selectedIds.includes(calc.id);
                const isRowExpanded = expandedRows.includes(calc.id);
                const bsAmount = parseFloat(calc.price)
                  ? Math.ceil(parseFloat(calc.price) * 6.96)
                  : 0;
                return (
                  <React.Fragment key={calc.id}>
                    <ListItem
                      disableGutters
                      sx={{
                        borderRadius: 2,
                        mb: 1.5,
                        bgcolor: isSelected
                          ? theme.palette.action.selected
                          : "background.paper",
                        transition: "background 0.2s, box-shadow 0.2s",
                        px: isSmallMobile ? 1.5 : 2.5,
                        py: isSmallMobile ? 1.2 : 1.7,
                        boxShadow: isSelected
                          ? "0 2px 8px 0 rgba(13,99,54,0.10)"
                          : "0 1px 4px 0 rgba(60,42,32,0.04)",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        // Evitar que el click en el chevron dispare el toggle de selección
                        if ((e.target as HTMLElement).closest(".chevron-btn"))
                          return;
                        handleToggleProduct(calc.id);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          onChange={() => handleToggleProduct(calc.id)}
                          tabIndex={-1}
                          disableRipple
                          color="primary"
                          sx={{ p: isSmallMobile ? 0.5 : 1 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemIcon>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flex: 1,
                          minWidth: 0,
                          justifyContent: "space-between",
                          gap: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: isSmallMobile ? "1.05rem" : "1.1rem",
                            color: "text.primary",
                            flex: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {calc.name}
                        </Typography>
                        {calc.price && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                              ml: 2,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 999,
                              bgcolor: "white",
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                color: isSelected
                                  ? "primary.main"
                                  : "text.secondary",
                                fontWeight: 600,
                                fontSize: isSmallMobile ? "0.98rem" : "1.03rem",
                                whiteSpace: "nowrap",
                                letterSpacing: 0.2,
                              }}
                            >
                              $
                              {parseFloat(calc.price).toLocaleString("es-BO", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              | Bs {bsAmount}
                            </Typography>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          className="chevron-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedRows((prev) =>
                              prev.includes(calc.id)
                                ? prev.filter((id) => id !== calc.id)
                                : [...prev, calc.id]
                            );
                          }}
                          sx={{
                            ml: 2,
                            flexShrink: 0,
                            borderRadius: 1.5,
                            p: 0.7,
                            bgcolor: isRowExpanded
                              ? "rgba(13,99,54,0.08)"
                              : "transparent",
                            transition: "background 0.2s",
                          }}
                          aria-label={isRowExpanded ? "Colapsar" : "Expandir"}
                        >
                          {isRowExpanded ? (
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="18 15 12 9 6 15"></polyline>
                              </svg>
                            </span>
                          ) : (
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          )}
                        </IconButton>
                      </Box>
                    </ListItem>
                    <Collapse
                      in={isRowExpanded && isSelected}
                      timeout="auto"
                      unmountOnExit
                    >
                      <PorcentajeTable total={bsAmount} />
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
            {/* Total como en ProductCalculator, pero con funcionalidad de mostrar tabla al click */}
            <Box
              sx={{
                textAlign: "center",
                userSelect: "none",
                mt: isSmallMobile ? 2 : 3,
                mb: isSmallMobile ? 1 : 2,
                cursor: "pointer",
                "&:hover .operation": { color: "primary.main" },
              }}
              onClick={() =>
                setExpandedRows((prev) =>
                  prev.includes("total")
                    ? prev.filter((id) => id !== "total")
                    : [...prev, "total"]
                )
              }
            >
              <Typography
                variant="h6"
                className="operation"
                sx={{
                  color: "text.secondary",
                  mb: 1,
                  transition: "color 0.2s",
                }}
              >
                {/* Mostrar la suma de los montos originales en USD */}$
                {totalAmount.toLocaleString("es-BO", {
                  maximumFractionDigits: 2,
                })}{" "}
                × 6.96
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", color: "primary.main" }}
              >
                = {totalBs.toLocaleString("es-BO")} Bs.
              </Typography>
            </Box>
            <Collapse in={expandedRows.includes("total")}>
              <PorcentajeTable total={totalBs} />
            </Collapse>
          </DialogContent>
          {/* Solo mostrar DialogActions/botón cerrar si NO es mobile */}
          {!isSmallMobile && (
            <DialogActions
              sx={{
                justifyContent: "center",
                pb: 2,
                pt: 0,
              }}
            >
              <Button
                onClick={() => setOpenSumModal(false)}
                color="primary"
                variant="contained"
                sx={{ fontSize: "1rem" }}
              >
                Cerrar
              </Button>
            </DialogActions>
          )}
        </Dialog>
      )}
    </Box>
  );
};

export default YanbalCalc;
